/**
 * AUT-01: Página de Inicio de Sesión
 *
 * Maneja:
 * - Flujo principal: login con email y contraseña
 * - SSO: login con Google OAuth2 (si habilitado para el tenant)
 * - E1: Credenciales inválidas
 * - E2: Cuenta inactiva
 * - E3: SSO only → oculta formulario
 * - A1: Enlace a recuperación de contraseña
 */
import { iniciarLoginGoogle } from '@/lib/googleOAuth'
import { GoogleIcon } from '@/components/GoogleIcon'
import { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertCircle, Eye, EyeOff, ShieldCheck } from 'lucide-react'
import { useLogin, useSSOConfig } from '@/modules/auth/hooks/useAuth'
import { rutaTrasLogin } from '@/modules/auth/rutaTrasLogin'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/Button'
import { EnvBanner } from '@/components/EnvBanner'

// ─── Validación con Zod ───────────────────────────────────────────────────────
const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'El email es requerido')
    .email('Falta un email válido'),
  password: z
    .string()
    .min(1, 'La contraseña es requerida'),
})

type LoginFormData = z.infer<typeof loginSchema>

// La URL de Google vive en `lib/googleOAuth.ts`: el portal la arma igual.

// ─── Componente ───────────────────────────────────────────────────────────────
export function LoginPage() {
  const navigate = useNavigate()
  const { mutate: login, isPending, error } = useLogin()
  const { tenants } = useAuthStore()
  const [showPassword, setShowPassword] = useState(false)
  const [ssoError, setSsoError] = useState<string | null>(null)

  // SSO config for current tenant
  const { data: ssoConfig, isLoading: ssoLoading } = useSSOConfig()

  const showPasswordForm = !ssoConfig || ssoConfig.auth_mode !== 'sso_only'
  const showGoogleButton = ssoConfig?.google_enabled && ssoConfig.google_client_id
  const showSAMLButton = ssoConfig?.saml_enabled === true

  // Prefill del email cuando se llega desde workspace-lookup (?email=...)
  const [searchParams] = useSearchParams()
  const prefilledEmail = searchParams.get('email') || ''

  // Motivo por el que se llegó al login (redirección automática al expirar o al
  // ser expulsado por sesión única). Informativo, no es un error de credenciales.
  const reason = searchParams.get('reason')
  const reasonMessage = reason === 'session_superseded'
    ? 'Tu sesión se cerró porque iniciaste sesión en otro dispositivo o navegador.'
    : reason === 'expired'
      ? 'Tu sesión expiró por inactividad. Inicia sesión nuevamente.'
      : null

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: prefilledEmail, password: '' },
  })

  const onSubmit = (data: LoginFormData) => {
    login(data, {
      // AUT-02 y #1656: selector si hay varias empresas, y si no, la pantalla
      // que se estaba abriendo antes de pedir el login.
      onSuccess: (response) => navigate(rutaTrasLogin(response)),
    })
  }

  const handleGoogleLogin = () => {
    if (!ssoConfig?.google_client_id) return
    setSsoError(null)
    iniciarLoginGoogle('erp').catch(() =>
      setSsoError('No se pudo iniciar el ingreso con Google. Intentar de nuevo.'))
  }

  const handleSAMLLogin = async () => {
    try {
      const res = await import('@/lib/apiClient').then(m =>
        m.default.post('/auth/saml/init/')
      )
      window.location.href = res.data.redirect_url
    } catch {
      setSsoError('No se pudo iniciar el ingreso con SSO. Intentar de nuevo.')
    }
  }

  const errorMessage = ssoError ?? (error
    ? (error as any)?.response?.data?.error?.message
      || (error as any)?.response?.data?.detail
      || 'Email o contraseña incorrectos. Por favor, intente de nuevo.'
    : null)

  return (
    <div className="min-h-screen flex flex-col bg-auth">
      <EnvBanner />
      <div className="flex-1 flex items-center justify-center px-4">
      {/* Card principal */}
      <div className="w-full max-w-md">
        {/* Logo / Marca */}
        <div className="text-center mb-8">
          <img src="/clobi-logo-white.svg" alt="Clobi" className="h-32 mx-auto mb-4" />
          <p className="text-content-subtle mt-1 text-sm">
            Renace tu empresa. Opera en la nube.
          </p>
        </div>

        {/* Formulario */}
        <div className="bg-surface rounded-2xl shadow-2xl p-8">
          <h2 className="text-xl font-semibold text-content mb-6">
            Iniciar sesión
          </h2>

          {/* Motivo de la redirección (sesión expirada / reemplazada) */}
          {reasonMessage && !errorMessage && (
            <div className="mb-4 p-3 rounded-lg bg-warning-surface border border-warning-border text-warning-content-strong text-sm flex items-start gap-2">
              <AlertCircle size={15} className="mt-0.5 shrink-0" />
              <span>{reasonMessage}</span>
            </div>
          )}

          {/* Error global */}
          {errorMessage && (
            <div className="mb-4 p-3 rounded-lg bg-danger-surface border border-danger-border text-danger-content text-sm flex items-start gap-2">
              <AlertCircle size={15} className="mt-0.5 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Password form */}
          {showPasswordForm && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-content mb-1.5">
                  Email
                </label>
                <input
                  {...register('email')}
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="tu@empresa.com"
                  className={`w-full px-4 py-2.5 rounded-lg border text-content placeholder:text-content-subtle
                    text-sm transition-colors outline-none
                    focus:ring-2 focus:ring-primary focus:border-primary
                    ${errors.email
                      ? 'border-danger-border-strong bg-danger-surface focus:ring-danger-solid focus:border-danger-solid'
                      : 'border-divider-strong bg-surface hover:border-divider-strong'
                    }`}
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-danger-content-soft">{errors.email.message}</p>
                )}
              </div>

              {/* Contraseña */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="password" className="block text-sm font-medium text-content">
                    Contraseña
                  </label>
                  <Link
                    to="/auth/forgot-password"
                    className="text-xs text-primary hover:text-primary-hover hover:underline"
                  >
                    ¿Olvidó su contraseña?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    {...register('password')}
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className={`w-full px-4 py-2.5 pr-10 rounded-lg border text-content placeholder:text-content-subtle
                      text-sm transition-colors outline-none
                      focus:ring-2 focus:ring-primary focus:border-primary
                      ${errors.password
                        ? 'border-danger-border-strong bg-danger-surface focus:ring-danger-solid focus:border-danger-solid'
                        : 'border-divider-strong bg-surface hover:border-divider-strong'
                      }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-muted transition-colors"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-xs text-danger-content-soft">{errors.password.message}</p>
                )}
              </div>

              {/* Botón de submit */}
              <Button type="submit" size="lg" fullWidth isPending={isPending} pendingLabel="Iniciando sesión...">
                Iniciar sesión
              </Button>
            </form>
          )}

          {/* Google SSO Button — debajo del formulario */}
          {showGoogleButton && (
            <>
              {showPasswordForm && (
                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-surface-muted" />
                  <span className="text-xs text-content-subtle font-medium">o</span>
                  <div className="flex-1 h-px bg-surface-muted" />
                </div>
              )}
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-3 py-2.5 px-4
                  border border-divider-strong rounded-lg text-sm font-medium text-content
                  hover:bg-soft transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <GoogleIcon />
                Iniciar sesión con Google
              </button>
            </>
          )}

          {/* SAML SSO Button */}
          {showSAMLButton && (
            <>
              {(showPasswordForm || showGoogleButton) && (
                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-surface-muted" />
                  <span className="text-xs text-content-subtle font-medium">o</span>
                  <div className="flex-1 h-px bg-surface-muted" />
                </div>
              )}
              <button
                type="button"
                onClick={handleSAMLLogin}
                className="w-full flex items-center justify-center gap-3 py-2.5 px-4
                  border border-divider-strong rounded-lg text-sm font-medium text-content
                  hover:bg-soft transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <ShieldCheck size={18} />
                Iniciar sesión con SSO
              </button>
            </>
          )}

          {/* SSO only — no password form */}
          {!showPasswordForm && !showGoogleButton && !showSAMLButton && (
            <p className="text-center text-sm text-content-muted">
              Este tenant requiere inicio de sesión con SSO, pero no hay proveedor configurado.
              Contacta al administrador.
            </p>
          )}
        </div>

        {/* El login del ERP es la puerta que la gente encuentra primero, y un
            trabajador sin acceso al ERP no tiene nada que hacer acá: sin este
            enlace llegaba a «tu cuenta no tiene acceso a ninguna empresa». */}
        <p className="text-center text-sm text-content-muted mt-5">
          ¿Solo se necesita ver liquidaciones, vacaciones o documentos?{' '}
          <Link to="/colaboradores/login" className="font-semibold text-primary hover:underline">
            Entrar al portal del colaborador
          </Link>
        </p>

        <p className="text-center text-content-muted text-xs mt-6">
          © 2026 Clobi by zen · Todos los derechos reservados
        </p>
      </div>
      </div>
    </div>
  )
}
